import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Observable, Subject, Subscription, from, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { CopomexService } from '../../services/copomex.service';
import { DataFormModel } from '../../models/data-form-model.model';
import { Neighborhood } from '../../models/neighborhood.model';
import { NeighborhoodService } from '../../services/neighborhood.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UtilService } from '../../services/util.service';
import { NeighborhoodTypeService } from '../../services/neighborhood-type.service';
import { ZipCode } from '../../models/zip-code.model';

@Component({
  selector: 'app-neighborhood-modal',
  templateUrl: './neighborhood-modal.component.html',
  styleUrl: './neighborhood-modal.component.scss'
})
export class NeighborhoodModalComponent implements OnInit, OnDestroy {

  @Input() neighborhood?: Neighborhood;

  // Código postal mexicano: exactamente 5 dígitos, con cero inicial permitido
  // (01000). La API exige 5 caracteres en el path; con menos responde 422.
  //
  // ⚠️ SE DECLARA ANTES DE dataForm A PROPÓSITO ⚠️
  // Los inicializadores de propiedades de clase corren en orden de escritura, y
  // el de dataForm lee this.zipCodePattern para armar el Validators.pattern del
  // campo. Si esta constante quedara declarada más abajo, en ese momento
  // valdría undefined y el validador de patrón nunca se aplicaría.
  //
  // Es UNA sola fuente para las DOS reglas: la que decide si el valor es válido
  // (Validators.pattern) y la que decide si se consulta a Copomex (el filter de
  // listenZipCodeChanges). Escribir la expresión dos veces es justo lo que
  // permitiría que un día dejen de coincidir.
  private readonly zipCodePattern: RegExp = /^\d{5}$/;

  public dataForm: DataFormModel = {
    // image: {
    //   value: '',
    //   type: 'image',
    //   label: '',
    //   required: false,
    //   validators: []
    // },
    neighborhoodTypeId: {
      value: null,
      type: 'select',
      idSelect: 'neighborhoodTypeId',
      labelSelect: 'name',
      data: [],
      label: 'Tipo de conjunto',
      placeholder: 'Seleccione el tipo de conjunto',
      requiredMessage: 'Por favor, seleccione el tipo de conjunto',
      required: true,
      validators: [Validators.required],
      onChange: (event: any) => {

      }
    },
    name: {
      value: '',
      type: 'text',
      label: 'Nombre del conjunto',
      requiredMessage: 'Por favor, ingrese el nombre del conjunto',
      required: true,
      validators: [Validators.required]
    },
    street: {
      value: '',
      type: 'text',
      label: 'Calle',
      requiredMessage: 'Por favor, ingrese la calle del conjunto',
      required: true,
      validators: [Validators.required]
    },
    // El orden VISUAL de los campos del modal sale exclusivamente del orden de
    // inserción de las llaves de este objeto: el @for del HTML recorre
    // utilService.objectKeys(dataForm) tal cual. Por eso el código postal se
    // mueve aquí, justo antes de Ciudad y Estado: es el campo que los rellena y
    // tiene que verse antes que ellos para que el flujo se entienda.
    // No hace falta tocar el template para reordenar.
    zipCode: {
      value: '',
      type: 'text',
      label: 'Código postal',
      requiredMessage: 'Por favor, ingrese el código postal del conjunto',
      required: true,
      // El código postal siempre son 5 dígitos, aquí y en la página de
      // Dirección: la misma regla no puede ser más laxa según por dónde se
      // capture. Validators.pattern NO se dispara con el campo vacío (devuelve
      // null ante valor vacío), así que required y pattern nunca marcan error
      // al mismo tiempo: vacío es 'required', 3 dígitos es 'pattern'.
      validators: [Validators.required, Validators.pattern(this.zipCodePattern)]
    },
    city: {
      value: '',
      type: 'text',
      label: 'Ciudad',
      requiredMessage: 'Por favor, ingrese la ciudad del conjunto',
      required: true,
      validators: [Validators.required]
    },
    state: {
      value: '',
      type: 'text',
      label: 'Estado',
      requiredMessage: 'Por favor, ingrese el estado del conjunto',
      required: true,
      validators: [Validators.required]
    },
    proofsRequired: {
      value: false,
      type: 'switch',
      label: 'Requiere de pruebas',
      requiredMessage: '',
      required: false,
    },
    platesRequired: {
      value: false,
      type: 'switch',
      label: 'Requiere de placas',
      requiredMessage: '',
      required: false,
    },
    brandRequired: {
      value: false,
      type: 'switch',
      label: 'Requiere de marca',
      requiredMessage: '',
      required: false,
    },
    modelRequired: {
      value: false,
      type: 'switch',
      label: 'Requiere de modelo',
      requiredMessage: '',
      required: false,
    },
    colorRequired: {
      value: false,
      type: 'switch',
      label: 'Requiere de color',
      requiredMessage: '',
      required: false,
    },
    accountsPerProperty: {
      value: '',
      type: 'number',
      label: 'Cuentas por propiedad',
      requiredMessage: 'Por favor, ingrese el número de cuentas por propiedad',
      required: true,
      validators: [Validators.required]
    },
    numberProperties: {
      value: '',
      type: 'number',
      label: 'Número de propiedades',
      requiredMessage: 'Por favor, ingrese el número de propiedades',
      required: true,
      validators: [Validators.required]
    },
    licencesNumber: {
      value: '',
      type: 'number',
      label: 'Número de licencias',
      requiredMessage: 'Por favor, ingrese el número de licencias',
      required: true,
      validators: [Validators.required]
    },
    devicesPerProperty: {
      value: '',
      type: 'number',
      label: 'Dispositivos por cuenta',
      requiredMessage: 'Por favor, ingrese el número de dispositivos por propiedad',
      required: true,
      validators: [Validators.required]
    },
    test: {
      value: true,
      type: 'switch',
      label: 'Prueba',
      requiredMessage: '',
      required: false,
    },
    status: {
      value: false,
      type: 'switch',
      label: 'Estado',
      requiredMessage: '',
      required: false,
    },
    passDuration: {
      value: '-1',
      type: 'number',
      label: 'Duración de los pases',
      requiredMessage: 'Por favor, ingrese la duración de los pases',
      required: true,
      validators: [Validators.required]
    },
    timeZone: {
      value: '-6',
      type: 'number',
      label: 'Zona horaria',
      requiredMessage: 'Por favor, ingrese la zona horaria',
      required: true,
      validators: [Validators.required]
    },
  };

  public submitted: boolean = false;
  public loading: boolean = false;

  public formNeighborhood!: FormGroup;

  // Llave del campo de código postal dentro de dataForm. El template la usa
  // para saber a qué input le toca el indicador de búsqueda y los mensajes,
  // sin tener que escribir la cadena a mano en el HTML.
  public readonly zipCodeKey: string = 'zipCode';

  // Estado de la consulta a Copomex, a nivel de campo. Ver el mismo trío en
  // address-page.component.ts: el error de un código postal pertenece a su
  // campo, no a un toast ni a un overlay global.
  public searchingZipCode: boolean = false;
  public zipCodeNotFound: boolean = false;
  public zipCodeSearchFailed: boolean = false;

  // El botón "Buscar" pulsado con un valor que no llega a 5 dígitos levanta
  // esta bandera para pintar el error de formato SIN encender `submitted`.
  //
  // Encender `submitted` desde el botón marcaría en rojo el modal entero
  // —tipo de conjunto, nombre, calle, cuentas por propiedad, licencias— por
  // haber pedido una búsqueda de código postal. La bandera acota el aviso al
  // único campo que participa.
  public zipCodeFormatWarned: boolean = false;

  // Canal del botón "Buscar": consulta A PETICIÓN EXPLÍCITA del usuario.
  //
  // ⚠️ NO PUEDE VIAJAR POR valueChanges ⚠️
  // Ese canal pasa por distinctUntilChanged(), y el caso principal de uso del
  // botón es pulsarlo SIN haber cambiado el código postal: el modal abre en
  // modo edición con el código ya guardado, la consulta no corre al abrir, y
  // el usuario quiere forzarla porque la ciudad o el estado están mal. Emitir
  // por el control haría además que el propio patchValue del autorrelleno se
  // considerara "escritura del usuario".
  //
  // Tampoco es una segunda suscripción independiente: ambos canales se unen
  // con merge() ANTES del filter y del switchMap, de modo que sigue habiendo
  // una sola petición en vuelo y un clic cancela una búsqueda automática
  // anterior (y una búsqueda automática cancela la del clic).
  private zipCodeSearchRequested: Subject<string> = new Subject<string>();

  // Se da de baja en ngOnDestroy. Es la única suscripción del componente.
  private zipCodeSubscription?: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    public copomexService: CopomexService,
    public neighborhoodService: NeighborhoodService,
    public neighborhoodTypeService: NeighborhoodTypeService,
    public utilService: UtilService,
  ) { }

  ngOnInit(): void {
    this.loadData();
  }

  ngOnDestroy(): void {
    this.zipCodeSubscription?.unsubscribe();
    this.zipCodeSearchRequested.complete();
  }

  get f() {
    return this.formNeighborhood.controls;
  }

  /**
   * GETTER zipCodeFormatErrorVisible
   *
   * Decide si se pinta "El código postal debe tener exactamente 5 dígitos" en
   * la franja de estado.
   *
   * Dos disparadores, uno viejo y uno nuevo:
   *   submitted + error 'pattern' → se intentó registrar (ya existía)
   *   zipCodeFormatWarned         → se pulsó Buscar con menos de 5 dígitos
   *
   * Las dos exclusiones mantienen la regla de UN SOLO MENSAJE A LA VEZ que ya
   * seguía este campo:
   *
   *   searchingZipCode → no puede convivir con "Buscando…". Si el usuario
   *     borra un dígito mientras una consulta sigue en vuelo, manda el
   *     indicador de carga.
   *
   *   submitted + error 'required' → el .invalid-feedback de arriba ya está
   *     pintando "Por favor, ingrese el código postal del conjunto", que dice
   *     exactamente lo mismo con más contexto. Sin esta salida, pulsar Buscar
   *     con el campo vacío después de un intento de registro fallido dejaría
   *     dos mensajes sobre el mismo campo.
   */
  get zipCodeFormatErrorVisible(): boolean {

    if(this.searchingZipCode) {
      return false;
    }

    if(this.submitted && this.f[this.zipCodeKey].errors?.['required']) {
      return false;
    }

    return this.zipCodeFormatWarned
      || (this.submitted && !!this.f[this.zipCodeKey].errors?.['pattern']);
  }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
   async create() {

    // this.formNeighborhood.value.imageBase64 = this.imageBase64;

    const flag = await this.neighborhoodService.create(
      this.formNeighborhood.value,
    );

    if(flag) {
      for(let key of Object.keys(this.dataForm)) {
        this.dataForm[key].value = '';
      }

      this.activeModal.close();
    }

  }

  /*
  ██╗      ██████╗  █████╗ ██████╗ ██████╗  █████╗ ████████╗ █████╗
  ██║     ██╔═══██╗██╔══██╗██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔══██╗
  ██║     ██║   ██║███████║██║  ██║██║  ██║███████║   ██║   ███████║
  ██║     ██║   ██║██╔══██║██║  ██║██║  ██║██╔══██║   ██║   ██╔══██║
  ███████╗╚██████╔╝██║  ██║██████╔╝██████╔╝██║  ██║   ██║   ██║  ██║
  ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝ ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚═╝  ╚═╝

  */
  async loadData() {
    this.loading = true;

    await this.neighborhoodTypeService.getAll();

    this.dataForm['neighborhoodTypeId'].data = this.neighborhoodTypeService.neighborhoodTypes;

    if(this.neighborhood) {

      this.dataForm = {
        ...this.dataForm,
      }

      for(let key of Object.keys(this.dataForm)) {

        if (key in this.neighborhood!) {
          const standardKey = key as keyof typeof this.neighborhood;
      
          // Ahora puedes acceder a `this.machine[machineKey]` sin errores de tipo.
          this.dataForm[key].value = this.neighborhood[standardKey]!;
        }
      } 
    }
    
    this.formNeighborhood = this.utilService.buildForm(this.dataForm);

    this.listenZipCodeChanges();

    this.loading = false;
  }

  /*
   ██████╗ ██████╗ ██████╗ ██╗ ██████╗  ██████╗
  ██╔════╝██╔═══██╗██╔══██╗██║██╔════╝ ██╔═══██╗
  ██║     ██║   ██║██║  ██║██║██║  ███╗██║   ██║
  ██║     ██║   ██║██║  ██║██║██║   ██║██║   ██║
  ╚██████╗╚██████╔╝██████╔╝██║╚██████╔╝╚██████╔╝
   ╚═════╝ ╚═════╝ ╚═════╝ ╚═╝ ╚═════╝  ╚═════╝

  */
  /**
   * FUNCTION listenZipCodeChanges
   *
   * Engancha la consulta a Copomex al control 'zipCode' del FormGroup.
   *
   * Se hace desde el TypeScript y NO enganchando un evento en el input del
   * template: ese input es genérico, el mismo @for lo pinta para name, street,
   * city y state, así que colgarle un (input) dispararía esta lógica en campos
   * que no tienen nada que ver con el código postal.
   *
   * ⚠️ ABRIR EL MODAL NO DISPARA LA CONSULTA ⚠️
   * utilService.buildForm construye cada control con
   * `new FormControl(obj[key].value, obj[key].validators)`, y el constructor de
   * FormControl NO emite en valueChanges: solo emiten setValue/patchValue y la
   * escritura del usuario. Por eso abrir el modal en modo edición con un código
   * postal ya cargado no consulta nada ni sobrescribe la ciudad y el estado que
   * ya estaban guardados.
   *
   * La suscripción se crea después de buildForm, que es donde el FormGroup
   * existe por primera vez.
   */
  private listenZipCodeChanges() {

    // loadData solo corre desde ngOnInit, pero si algún día se llamara dos
    // veces la suscripción anterior quedaría huérfana apuntando a un FormGroup
    // viejo. Se cierra antes de abrir la nueva.
    this.zipCodeSubscription?.unsubscribe();

    // Rama del AUTORRELLENO: lo que el usuario teclea en el control. Es la
    // única que lleva debounce y distinctUntilChanged; el botón se salta las
    // dos (ver zipCodeSearchRequested).
    const typedZipCodes: Observable<string> = this.formNeighborhood.get(this.zipCodeKey)!.valueChanges.pipe(

      // El usuario teclea 5 dígitos en menos de medio segundo; sin debounce se
      // dispararían hasta 5 peticiones para llegar al mismo resultado.
      debounceTime(400),

      // La normalización sube por delante de distinctUntilChanged para que la
      // comparación se haga sobre el mismo texto que después se manda a la
      // API, y para que las dos ramas del merge emitan string.
      map((value) => `${value ?? ''}`.trim()),

      distinctUntilChanged(),

      tap(() => {
        // Cualquier cambio en el campo invalida los mensajes de la búsqueda
        // anterior, aunque el valor todavía no llegue a 5 dígitos, y también
        // el aviso de formato que hubiera dejado el botón.
        this.zipCodeNotFound = false;
        this.zipCodeSearchFailed = false;
        this.zipCodeFormatWarned = false;
      })
    );

    // merge() une las dos fuentes ANTES del filter y del switchMap: el clic y
    // la escritura comparten el mismo operador de cancelación, así que jamás
    // hay dos peticiones en vuelo ni respuestas que lleguen desordenadas.
    this.zipCodeSubscription = merge(typedZipCodes, this.zipCodeSearchRequested).pipe(

      // El código postal va como path parameter y la API exige exactamente 5
      // caracteres: con menos responde 422. Aquí se corta antes de pedir.
      filter((value) => this.zipCodePattern.test(value)),

      tap(() => {
        this.searchingZipCode = true;
      }),

      // switchMap NO es opcional.
      // La API corre en Lambda sin concurrencia provisionada: el arranque en
      // frío puede tardar de 1 a 3 segundos. Si el usuario corrige un dígito
      // mientras la primera petición sigue en vuelo, habría dos respuestas en
      // camino y podrían llegar desordenadas, dejando la ciudad del código
      // postal viejo. switchMap cancela la anterior: siempre gana la última.
      switchMap((value) =>
        from(this.copomexService.searchByZipCode(value)).pipe(
          map((zipCodes) => ({ zipCodes, failed: false })),

          // El catchError va DENTRO del switchMap a propósito: si estuviera
          // fuera, el primer error de red mataría la suscripción y el campo
          // dejaría de consultar mientras el modal siguiera abierto.
          catchError(() => of({ zipCodes: [] as ZipCode[], failed: true }))
        )
      )

    ).subscribe((result) => {

      this.searchingZipCode = false;

      if(result.failed) {
        // Error de red o del servidor. NO se toca Ciudad ni Estado.
        this.zipCodeSearchFailed = true;
        return;
      }

      // Un código postal inexistente NO devuelve 404: devuelve 200 con la
      // lista vacía. Esta es la única forma de detectar "no encontrado".
      if(result.zipCodes.length === 0) {
        // Tampoco se toca Ciudad ni Estado aquí: el usuario podría tener
        // capturados valores válidos.
        this.zipCodeNotFound = true;
        return;
      }

      this.fillAddressFromZipCode(result.zipCodes[0]);
    });

  }

  /**
   * FUNCTION searchZipCode
   *
   * Entrada EXPLÍCITA de la consulta a Copomex: el botón "Buscar".
   *
   * Existe porque la consulta automática nunca corre al abrir el modal (el
   * constructor de FormControl no emite en valueChanges, ver
   * listenZipCodeChanges). En modo edición, si el código postal guardado es
   * correcto pero la ciudad o el estado están mal, sin este botón la única
   * forma de forzar la consulta sería borrar un dígito y volver a escribirlo.
   *
   * Emite por `zipCodeSearchRequested`, NO por el control: ver el comentario
   * de esa propiedad.
   *
   * El botón NO se deshabilita con menos de 5 dígitos —mismo criterio que el
   * botón Registrar/Actualizar—: se pulsa y se pinta el error de formato junto
   * al campo. Deshabilitarlo dejaría al usuario adivinando qué le falta.
   */
  searchZipCode() {

    const zipCode = `${this.f[this.zipCodeKey].value ?? ''}`.trim();

    // Se pidió una búsqueda nueva: lo que dijera la anterior ya no aplica.
    this.zipCodeNotFound = false;
    this.zipCodeSearchFailed = false;

    if(!this.zipCodePattern.test(zipCode)) {
      // Vacío y "123" caen los dos aquí: en ambos casos lo que falta es un
      // código de 5 dígitos. El texto no se escribe en TypeScript, ya vive en
      // la plantilla y es el mismo que usa la página de Dirección.
      this.zipCodeFormatWarned = true;
      return;
    }

    this.zipCodeFormatWarned = false;

    this.zipCodeSearchRequested.next(zipCode);
  }

  /**
   * FUNCTION fillAddressFromZipCode
   *
   * Escribe Ciudad y Estado a partir del resultado de SEPOMEX.
   *
   * MAPEO DE CAMPOS —decisión tomada, no cambiar sin motivo:
   *   Estado ← state
   *   Ciudad ← municipality  (NO city)
   *
   * `city` puede llegar como cadena vacía en localidades sin ciudad asignada,
   * mientras que `municipality` siempre viene poblado. Además `municipality`
   * es lo que coincide con los datos ya capturados en base de datos: Pachuca
   * de Soto, Lagos de Moreno y Tlalpan son municipios, no ciudades.
   *
   * Todos los elementos del array comparten `state` y `municipality` (varían
   * en la colonia), así que basta con leer el primero.
   *
   * El autorrelleno NO guarda: solo escribe en el formulario. El usuario sigue
   * teniendo que pulsar Registrar/Actualizar. Y Ciudad y Estado siguen siendo
   * editables, porque los datos históricos no siempre coinciden con SEPOMEX.
   *
   * @param zipCode primer resultado devuelto por Copomex
   */
  private fillAddressFromZipCode(zipCode: ZipCode) {

    this.formNeighborhood.patchValue({
      city: zipCode.municipality,
      state: zipCode.state
    });
  }

  /*
   ██████╗ ███╗   ██╗███████╗██╗██╗     ███████╗ ██████╗██╗  ██╗ █████╗ ███╗   ██╗ ██████╗ ███████╗
  ██╔═══██╗████╗  ██║██╔════╝██║██║     ██╔════╝██╔════╝██║  ██║██╔══██╗████╗  ██║██╔════╝ ██╔════╝
  ██║   ██║██╔██╗ ██║█████╗  ██║██║     █████╗  ██║     ███████║███████║██╔██╗ ██║██║  ███╗█████╗
  ██║   ██║██║╚██╗██║██╔══╝  ██║██║     ██╔══╝  ██║     ██╔══██║██╔══██║██║╚██╗██║██║   ██║██╔══╝
  ╚██████╔╝██║ ╚████║██║     ██║███████╗███████╗╚██████╗██║  ██║██║  ██║██║ ╚████║╚██████╔╝███████╗
   ╚═════╝ ╚═╝  ╚═══╝╚═╝     ╚═╝╚══════╝╚══════╝ ╚═════╝╚═╝  ╚═╝╚═╝  ╚═╝╚═╝  ╚═══╝ ╚═════╝ ╚══════╝

  */
  /**
   * FUNCTION onFileChange
   * 
   * @param event 
   */
  onFileChange(event: any) {
    // console.log(event);

    // const file = event.target.files[0];

    // const reader = new FileReader();
    // reader.readAsDataURL(file); // toBase64
    // reader.onload = () => {
    //   this.imageBase64 = reader.result as string; // base64 Image src
    //   console.log(this.imageBase64);
      
    // };
    
  }

  /*
   ██████╗ ███╗   ██╗███████╗██╗   ██╗██████╗ ███╗   ███╗██╗████████╗
  ██╔═══██╗████╗  ██║██╔════╝██║   ██║██╔══██╗████╗ ████║██║╚══██╔══╝
  ██║   ██║██╔██╗ ██║███████╗██║   ██║██████╔╝██╔████╔██║██║   ██║
  ██║   ██║██║╚██╗██║╚════██║██║   ██║██╔══██╗██║╚██╔╝██║██║   ██║
  ╚██████╔╝██║ ╚████║███████║╚██████╔╝██████╔╝██║ ╚═╝ ██║██║   ██║
   ╚═════╝ ╚═╝  ╚═══╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝     ╚═╝╚═╝   ╚═╝

  */
  async onSubmit() {

    console.log(this.formNeighborhood.value);
    
    this.submitted = true;

    if(this.formNeighborhood.invalid) {
      return;
    }

    this.formNeighborhood.value.accountsPerProperty = parseInt(this.formNeighborhood.value.accountsPerProperty);
    this.formNeighborhood.value.numberProperties = parseInt(this.formNeighborhood.value.numberProperties);
    this.formNeighborhood.value.licencesNumber = parseInt(this.formNeighborhood.value.licencesNumber);
    this.formNeighborhood.value.devicesPerProperty = parseInt(this.formNeighborhood.value.devicesPerProperty);
    this.formNeighborhood.value.passDuration = parseInt(this.formNeighborhood.value.passDuration);
    this.formNeighborhood.value.timeZone = parseInt(this.formNeighborhood.value.timeZone);

    console.log(this.formNeighborhood.value);

    if(this.neighborhood) {

      await this.update();
    } else {
      await this.create();
    }
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update() {

    this.formNeighborhood.value.neighborhoodId = this.neighborhood!.neighborhoodId;

    // this.formNeighborhood.value.imageBase64 = this.imageBase64;

    // const flag = await this.neighborhoodService.update(
    // );

    // if(flag) {

    //   for(let key of Object.keys(this.dataForm)) {
    //     this.dataForm[key].value = '';
    //   }

    //   this.activeModal.close();
    // }
  }

}
